import React, { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronUp, RotateCcw } from 'lucide-react';
import axios from '../lib/axios';
import { API } from '../App';
import SafariDropdown from './ui/safari-dropdown';

/**
 * DynamicFilters - Renders category-specific filterable properties
 * 
 * Features:
 * - Fetches filterable properties for the selected category
 * - Supports text, number (with range), predefined_list, and boolean types
 * - Collapsible sections grouped by property group
 * - URL-synced filter state
 */
const DynamicFilters = ({ 
  categoryId, 
  subcategory,
  activeFilters = {}, // Current props_* filters from parent
  onFilterChange,     // Callback: (key, value) => void
  onFiltersReset,     // Callback to reset all dynamic filters
  showTitle = true,   // Whether to show the "Category Filters" title
  flat = false,       // Flat mode: no collapsible groups, all fields visible
  className = ''      // Additional CSS classes
}) => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});

  // Fetch filterable properties when category changes
  useEffect(() => {
    if (!categoryId) {
      setProperties([]);
      return;
    }

    const fetchProperties = async () => {
      setLoading(true);
      try {
        let url = `${API}/categories/${categoryId}/properties?lang=en`;
        if (subcategory) {
          url += `&subcategory=${encodeURIComponent(subcategory)}`;
        }
        const response = await axios.get(url);
        // Only keep filterable properties
        const filterable = (response.data.properties || []).filter(p => p.filterable);
        setProperties(filterable);
        
        // Auto-expand first group
        if (filterable.length > 0) {
          const groups = [...new Set(filterable.map(p => p.group || 'Other'))];
          setExpandedGroups({ [groups[0]]: true });
        }
      } catch (error) {
        console.error('Failed to fetch properties:', error);
        setProperties([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, [categoryId, subcategory]);

  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  // Get grouped properties (with initial value to prevent reduce error on empty array)
  const groupedProperties = properties.reduce((acc, prop) => {
    const group = prop.group || 'Other';
    if (!acc[group]) acc[group] = [];
    acc[group].push(prop);
    return acc;
  }, {});

  const groups = Object.keys(groupedProperties);

  // Count active dynamic filters
  const activeCount = Object.keys(activeFilters).filter(k => k.startsWith('props_') && activeFilters[k]).length;

  if (!categoryId || properties.length === 0) {
    return null;
  }

  return (
    <div className={`dynamic-filters ${className}`} data-testid="dynamic-filters">
      {/* Header */}
      {showTitle && (
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-foreground">
            Category Filters
            {activeCount > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">
                {activeCount} active
              </span>
            )}
          </h3>
          {activeCount > 0 && (
            <button
              onClick={onFiltersReset}
              className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
              data-testid="reset-dynamic-filters"
            >
              <RotateCcw className="w-3 h-3" />
              Reset
            </button>
          )}
        </div>
      )}
      
      {/* Reset button when title is hidden but filters are active */}
      {!showTitle && activeCount > 0 && (
        <div className="flex justify-end mb-3">
          <button
            onClick={onFiltersReset}
            className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
            data-testid="reset-dynamic-filters"
          >
            <RotateCcw className="w-3 h-3" />
            Reset filters ({activeCount})
          </button>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-10 skeleton rounded-lg" />
          ))}
        </div>
      ) : flat ? (
        /* Flat mode: All fields visible without collapsible groups - better for mobile */
        <div className="space-y-4">
          {properties.map(prop => (
            <FilterField
              key={prop.key}
              property={prop}
              value={activeFilters[`props_${prop.key}`]}
              minValue={activeFilters[`props_${prop.key}_min`]}
              maxValue={activeFilters[`props_${prop.key}_max`]}
              onChange={onFilterChange}
            />
          ))}
        </div>
      ) : (
        /* Grouped mode with collapsible sections */
        <div>
          {groups.map(group => (
            <div key={group} className="border border-border rounded-lg overflow-hidden mb-3 last:mb-0">
              {/* Group Header */}
              <button
                onClick={() => toggleGroup(group)}
                className="w-full flex items-center justify-between px-3 py-2 bg-secondary/50 hover:bg-secondary transition-colors"
                data-testid={`filter-group-${group.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <span className="text-sm font-medium text-foreground">{group}</span>
                {expandedGroups[group] ? (
                  <ChevronUp className="w-4 h-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                )}
              </button>

              {/* Group Content */}
              {expandedGroups[group] && (
                <div className="p-3 space-y-3">
                  {groupedProperties[group].map(prop => (
                    <FilterField
                      key={prop.key}
                      property={prop}
                      value={activeFilters[`props_${prop.key}`]}
                      minValue={activeFilters[`props_${prop.key}_min`]}
                      maxValue={activeFilters[`props_${prop.key}_max`]}
                      onChange={onFilterChange}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * FilterField - Renders the appropriate input for a property type
 */
const FilterField = ({ property, value, minValue, maxValue, onChange }) => {
  const { key, name, type, options, unit, min_value, max_value } = property;
  const label = typeof name === 'object' ? name.en : name;

  // Debounce for range inputs
  const [localMin, setLocalMin] = useState(minValue || '');
  const [localMax, setLocalMax] = useState(maxValue || '');

  useEffect(() => {
    setLocalMin(minValue || '');
    setLocalMax(maxValue || '');
  }, [minValue, maxValue]);

  const handleRangeBlur = useCallback((field, val) => {
    onChange(`props_${key}_${field}`, val || '');
  }, [key, onChange]);

  // Boolean filter (checkbox style)
  if (type === 'boolean') {
    const isChecked = value === 'true' || value === true;
    return (
      <label 
        className="flex items-center gap-2 cursor-pointer group" 
        data-testid={`filter-${key}`}
      >
        <div className="relative">
          <input
            type="checkbox"
            checked={isChecked}
            onChange={(e) => onChange(`props_${key}`, e.target.checked ? 'true' : '')}
            className="sr-only peer"
          />
          <div className={`w-5 h-5 rounded border-2 transition-all duration-200 flex items-center justify-center
            ${isChecked 
              ? 'bg-primary border-primary' 
              : 'border-border bg-card group-hover:border-primary/50'
            }`}
          >
            {isChecked && (
              <svg className="w-3 h-3 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
        </div>
        <span className={`text-sm transition-colors ${isChecked ? 'text-primary font-medium' : 'text-foreground group-hover:text-primary'}`}>
          {label}
        </span>
      </label>
    );
  }

  // Predefined list (dropdown)
  if (type === 'predefined_list' && options?.length) {
    // Build options array for SafariDropdown
    const dropdownOptions = [
      { value: '', label: `Any ${label}` },
      ...options.map(opt => {
        const optValue = typeof opt === 'object' ? (opt.value || String(opt)) : String(opt);
        const optLabel = typeof opt === 'object' ? (opt.label || opt.value || String(opt)) : String(opt);
        return { value: optValue, label: optLabel };
      })
    ];

    return (
      <div data-testid={`filter-${key}`}>
        <label className="block text-xs text-muted-foreground mb-1">{label}</label>
        <SafariDropdown
          value={value || ''}
          onChange={(val) => onChange(`props_${key}`, val)}
          options={dropdownOptions}
          placeholder={`Any ${label}`}
          className="w-full"
          testId={`filter-dropdown-${key}`}
        />
      </div>
    );
  }

  // Number filter (range inputs)
  if (type === 'number') {
    return (
      <div data-testid={`filter-${key}`}>
        <label className="block text-xs text-muted-foreground mb-1">
          {label} {unit && <span className="text-muted-foreground/60">({unit})</span>}
        </label>
        <div className="flex gap-2">
          <input
            type="number"
            placeholder={min_value ? `Min ${min_value}` : 'Min'}
            value={localMin}
            onChange={(e) => setLocalMin(e.target.value)}
            onBlur={() => handleRangeBlur('min', localMin)}
            className="flex-1 px-3 py-2.5 text-sm border border-border rounded-xl bg-card text-foreground focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none w-20"
          />
          <span className="text-muted-foreground self-center">-</span>
          <input
            type="number"
            placeholder={max_value ? `Max ${max_value}` : 'Max'}
            value={localMax}
            onChange={(e) => setLocalMax(e.target.value)}
            onBlur={() => handleRangeBlur('max', localMax)}
            className="flex-1 px-3 py-2.5 text-sm border border-border rounded-xl bg-card text-foreground focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none w-20"
          />
        </div>
      </div>
    );
  }

  // Text filter (simple input)
  return (
    <div data-testid={`filter-${key}`}>
      <label className="block text-xs text-muted-foreground mb-1">{label}</label>
      <input
        type="text"
        placeholder={`Search ${label.toLowerCase()}...`}
        value={value || ''}
        onChange={(e) => onChange(`props_${key}`, e.target.value)}
        className="w-full px-3 py-2.5 text-sm border border-border rounded-xl bg-card text-foreground focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none"
      />
    </div>
  );
};

export default DynamicFilters;
